import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import SearchBar from "@/components/SearchBar";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib/";
import { IconCheck } from "@velaro/velaro-component-lib/";
import AccelaPanel from "./AccelaPanel";
import { getScopeOptions } from "./AccelaScopeOptions";

const handleSearch = (searchText: string) => {};

export interface AccelaMap {
  id: number;
  accelaField: any;
  label: string;
  create: boolean;
  usedForSearching: boolean;
  showWhenSelected: boolean;
  showInSearch: boolean;
  defaultValue: string;
  scopeType: string;
}

interface Props {
  teamId: number;
  selectedConfigId: number;
}

export default function AccelaDataMapping(props: Props) {
  const [panels, setPanels] = useState<{ id: number; scopeType: string }[]>([]);
  const [showSaved, setShowSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const api = useApi();
  const scopeOptions = getScopeOptions();

  const addPanel = () =>
    setPanels([...panels, { id: panels.length + 1, scopeType: "" }]);

  const showSavedMessage = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  const fetchMappingFieldCount = useCallback(
    async (scopeType: string) => {
      if (!scopeType || props.selectedConfigId === 0) {
        return { scopeType, dataLength: 0 };
      }
      try {
        const resp = await api.messaging.get(
          `Accela/Integrations/Mapping/${scopeType}/${props.selectedConfigId}`
        );
        if (!resp.ok) throw new Error("Failed to fetch data");

        const json = await resp.json();
        return {
          scopeType,
          dataLength: Array.isArray(json) ? json.length : 0
        };
      } catch (error) {
        console.error(`Error fetching mapping for ${scopeType}:`, error);
        return { scopeType, dataLength: 0 };
      }
    },
    [api.messaging, props.selectedConfigId]
  );

  const fetchAllMappings = async () => {
    setPanels([]);
    setLoading(true);

    const panelPromises = scopeOptions
      .filter((option) => option !== "")
      .map(fetchMappingFieldCount);

    const results = await Promise.all(panelPromises);

    const panelsToAdd = results
      .filter(({ dataLength }) => dataLength > 0)
      .map(({ scopeType }, index) => ({ id: index + 1, scopeType }));

    setPanels(panelsToAdd);
    setLoading(false);
  };

  useEffect(() => {
    if (props.selectedConfigId != undefined && props.selectedConfigId > 0) {
      fetchAllMappings();
    }
  }, [props.selectedConfigId]);

  useEffect(() => {
    showSavedMessage();
  }, []);

  return (
    <div>
      <div className="justify-between items-center inline-flex w-full">
        <div className="justify-start items-start flex">
          <SearchBar placeholderText="Search" onSearch={handleSearch} />
        </div>

        <div className="justify-end items-center gap-3 flex w-full">
          {showSaved && (
            <div className="flex items-center gap-2 text-slate-500">
              <span className="filter invert">
                {React.createElement(IconCheck, {
                  className: "text-slate-500"
                })}
              </span>
              <div className="text-slate-500 text-sm font-medium">
                All changes saved
              </div>
            </div>
          )}
          <PrimaryBlueButton
            label="Add Mapping"
            onClick={addPanel}
            className="font-medium h-full"
            prefixIcon={React.createElement(IconPlus)}
          />
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center my-4">
          <span>Loading mappings...</span>
        </div>
      )}

      <div className="mt-4">
        {panels.map((panel) => (
          <AccelaPanel
            key={panel.id}
            teamId={props.teamId!}
            scopeType={panel.scopeType}
            showSavedMessage={showSavedMessage}
            selectedConfigId={props.selectedConfigId}
            isDeleteDisabled={panels.length === 1}
          />
        ))}
      </div>
    </div>
  );
}
